import { colors } from '@alixpartners/ui-components';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import isNumber from 'lodash/isNumber';
import isArray from 'lodash/isArray';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

export const ITEMS_TYPE = {
	CATEGORY: 1,
	VENDOR: 2,
	PT_HARMONIZATION: 4,
	PT_IMPROVEMENT: 3,
	TAIL_SPEND: 5,
};

export const TAXONOMY_VALUES = {
	INDIRECT_AP: '1_3',
	INDIRECT_CLIENT: '1_2',
	INDIRECT_PROJECT: '1_1',
	DIRECT_PROJECT: '0_1',
	DIRECT_CLIENT: '0_2',
};

export const SPEND_TYPE_LABELS = ['Direct', 'Indirect'];
export const TAXONOMY_LABELS = ['', 'project', 'client', 'AP'];
export const CATEGORY_PARAM = ['', 'category', 'clientCategory', 'apcategory'];

export const STEPS_CONFIG = [
	{ label: 'Choose initiative type', step: 1 },
	{
		label: 'Edit tail spend settings',
		step: 4,
		requiredType: ITEMS_TYPE.TAIL_SPEND,
	},
	{ label: 'Select opportunity items', step: 2 },
	{ label: 'Configure initiative', step: 3 },
];

export const TAIL_SPEND_TYPE = 'Tail spend';

const CATEGORY_TYPES = [
	{ label: 'Sourcing', value: 'Sourcing' },
	{ label: 'Spec rationalisation', value: 'Spec rationalisation' },
	{ label: 'Supplier negotiations', value: 'Supplier negotiations' },
	{ label: 'TSR / DTV', value: 'TSR / DTV' },
	{ label: 'Others', value: 'Others' },
	{ label: 'Tail spend', value: TAIL_SPEND_TYPE },
];

export const INITIATIVE_TYPES = {
	[ITEMS_TYPE.CATEGORY]: CATEGORY_TYPES,
	[ITEMS_TYPE.VENDOR]: [{ label: 'Vendors', value: 'Vendors' }],
	[ITEMS_TYPE.PT_HARMONIZATION]: [
		{
			label: 'Payment term harmonization',
			value: 'Payment term harmonization',
		},
	],
	[ITEMS_TYPE.PT_IMPROVEMENT]: [
		{ label: 'Payment term improvement', value: 'Payment term improvement' },
	],
	[ITEMS_TYPE.TAIL_SPEND]: CATEGORY_TYPES,
};

export const ITEMS_TYPES = [
	{
		label: 'Categories',
		value: ITEMS_TYPE.CATEGORY,
		values: [ITEMS_TYPE.CATEGORY, ITEMS_TYPE.TAIL_SPEND],
	},
	{ label: 'Vendors', value: ITEMS_TYPE.VENDOR },
	{
		label: 'Payment Terms Harmonization',
		value: ITEMS_TYPE.PT_HARMONIZATION,
	},
	{
		label: 'Payment Terms Improvement',
		value: ITEMS_TYPE.PT_IMPROVEMENT,
	},
];

export const GROUP_OPTIONS = [
	{ label: 'None', value: 0 },
	{ label: 'Group by L1', value: 1 },
	{ label: 'Group by L2', value: 2 },
	{ label: 'Group by L3', value: 3 },
	{ label: 'Group by L4', value: 4 },
];

export const FILTER_OPTIONS = [{ label: 'All categories', value: '' }];

export const DEFAULT_PARAMS = {
	harmonized: true,
	isPmi: true,
	currencyType: 'fixed',
	filters: ['sourceable'],
	filterValues: [true],
	splitSpendByDimension: 'company',
	disableTailSpendSegments: true,
};

export const TAXONOMY_GROUPS = {
	indirect: 'Indirect spend',
	direct: 'Direct spend',
};

export const TAXONOMY_DATA = [
	{
		label: 'Project taxonomy',
		value: TAXONOMY_VALUES.DIRECT_PROJECT,
		group: 'direct',
	},
	{
		label: 'Client taxonomy',
		value: TAXONOMY_VALUES.DIRECT_CLIENT,
		group: 'direct',
	},
	{
		label: 'Project taxonomy',
		value: TAXONOMY_VALUES.INDIRECT_PROJECT,
		group: 'indirect',
	},
	{
		label: 'AP taxonomy',
		value: TAXONOMY_VALUES.INDIRECT_AP,
		group: 'indirect',
	},
	{
		label: 'Client taxonomy',
		value: TAXONOMY_VALUES.INDIRECT_CLIENT,
		group: 'indirect',
	},
];

export const converDataToTree = data => {
	const stack = [];
	const result = [];

	data.forEach(dataItem => {
		if (
			(!dataItem.value && !dataItem.tailValue) ||
			(dataItem.isTailVendorsNode && dataItem.parentId === '/') ||
			!dataItem.label
		) {
			return;
		}

		const convertedItem = convertItem(dataItem);

		if (stack.length) {
			while (stack.length) {
				let parentItem = stack[stack.length - 1];

				if (parentItem.id === dataItem.parentId) {
					if (dataItem.isTailVendorsNode) {
						Object.entries(convertedItem.companies).forEach(([name, value]) => {
							parentItem.companies[name] = parentItem.companies[name]
								? parentItem.companies[name] + value
								: value;
						});
						break;
					} else {
						parentItem.children.push(convertedItem);
						stack.push(convertedItem);
						break;
					}
				} else {
					stack.pop();
				}
			}

			if (!stack.length) {
				result.push(convertedItem);
				stack.push(convertedItem);
			}
		} else {
			result.push(convertedItem);
			stack.push(convertedItem);
		}
	});

	return result;
};

export const groupSegments = data => {
	return data.reduce((acc, item) => {
		const existingItem = acc.find(node => node.id === item.id);

		if (existingItem) {
			existingItem.companies = Object.keys(item.companies).reduce(
				(companies, companyName) => {
					const resultCompaniesObj = { ...companies };

					if (resultCompaniesObj[companyName]) {
						resultCompaniesObj[companyName] =
							resultCompaniesObj[companyName] + item.companies[companyName];
					} else {
						resultCompaniesObj[companyName] = item.companies[companyName];
					}

					return resultCompaniesObj;
				},
				{ ...existingItem.companies }
			);

			if (item.children) {
				existingItem.children = groupSegments([
					...(existingItem.children ? existingItem.children : []),
					...item.children,
				]);
			}

			existingItem.spend = existingItem.spend + item.spend;
			existingItem.segments = {
				...existingItem.segments,
				[item.segmentName]: item.spend,
			};
			existingItem.segmentsByCompanies = {
				...existingItem.segmentsByCompanies,
				[item.segmentName]: item.companies,
			};

			return acc;
		}

		const itemWithSegments =
			!item.segments && item.segmentName
				? {
						...omit(item, 'segmentName'),
						segments: { [item.segmentName]: item.spend },
						segmentsByCompanies: { [item.segmentName]: item.companies },
						children: groupSegments(item.children),
				  }
				: item;

		return [...acc, itemWithSegments];
	}, []);
};

export const getHierarchyFromId = id => {
	return id
		.split('/')
		.filter(item => item)
		.map(item => decodeURIComponent(item));
};

export const convertItem = item => {
	const {
		label,
		id,
		splitByDimension,
		depth,
		value,
		tailValue,
		segmentName,
		isTailVendorsNode,
	} = item;

	const result = {
		id,
		name: label,
		spend: isTailVendorsNode ? value : value + tailValue,
		headSpend: !isTailVendorsNode ? value : 0,
		tailSpend: isTailVendorsNode ? value : tailValue,
		children: [],
		level: depth,
		companies: { ...splitByDimension },
		groupName: id,
		segmentName,
		hierarchy: getHierarchyFromId(id),
		includeTailSpend: true,
	};

	return result;
};

export const groupByLevel = (nodes, level) => {
	if (!level) {
		return nodes.reduce((acc, item) => {
			return [...acc, ...getLeaves(item.children)];
		}, []);
	}

	const groupped = flatLevel(nodes, level);

	return groupped.map(item => {
		return { ...item, children: getLeaves(item.children) };
	});
};

export const flatLevel = (nodes, level) => {
	if (nodes[0] && nodes[0].level === level) {
		return nodes;
	}

	return nodes.reduce((acc, item) => {
		return [...acc, ...flatLevel(item.children, level)];
	}, []);
};

export const getLeaves = nodes => {
	return nodes.reduce((acc, item) => {
		const children = item.children ? getLeaves(item.children) : [];

		if (children.length === 0) {
			return [...acc, item];
		}

		return [...acc, ...children];
	}, []);
};

export const aggregateLeavesByName = (nodes, parent) => {
	return nodes.reduce((acc, node) => {
		if (node.children && node.children.length > 0) {
			return [
				...acc,
				{ ...node, children: aggregateLeavesByName(node.children, node) },
			];
		}

		const existingNodeIndex = acc.findIndex(item => item.name === node.name);
		const existingNode =
			existingNodeIndex !== -1 ? acc[existingNodeIndex] : null;

		if (!existingNode) {
			return [
				...acc,
				{
					...node,
					id: parent ? `${parent.id}${node.name}` : node.name,
					hierarchy: parent ? parent.hierarchy : [],
					groupName: parent ? parent.id : '',
				},
			];
		}

		const aggregatedCompanies = Object.keys(node.companies).reduce(
			(companyObj, companyName) => {
				if (companyObj[companyName]) {
					return {
						...companyObj,
						[companyName]:
							companyObj[companyName] + node.companies[companyName],
					};
				}

				return {
					...companyObj,
					[companyName]: node.companies[companyName],
				};
			},
			existingNode.companies
		);

		const aggregatedNode = {
			...existingNode,
			id: parent ? `${parent.id}${existingNode.name}` : existingNode.name,
			hierarchy: parent ? parent.hierarchy : [],
			groupName: parent ? parent.id : '',
			spend: existingNode.spend + node.spend,
			headSpend: existingNode.headSpend + node.headSpend,
			tailSpend: existingNode.tailSpend + node.tailSpend,
			companies: aggregatedCompanies,
		};

		return Object.assign([], acc, { [existingNodeIndex]: aggregatedNode });
	}, []);
};

export const filterGrouped = (nodes, filterFunc) => {
	const isGrouped =
		nodes.length && nodes[0].children && nodes[0].children.length > 0;

	if (!isGrouped) {
		return nodes.filter(filterFunc);
	}

	return nodes.reduce((acc, item) => {
		if (item.children && item.children.length > 0) {
			const filteredChildren = item.children.filter(filterFunc);
			const aggregatedSpend = filteredChildren.reduce(
				(accSpend, item) => accSpend + item.spend,
				0
			);

			if (filteredChildren.length > 0) {
				const aggregatedProps = filteredChildren.reduce(
					(acc, item) => {
						const companies = Object.keys(item.companies).reduce(
							(companyObj, companyName) => {
								if (companyObj[companyName]) {
									return {
										...companyObj,
										[companyName]:
											companyObj[companyName] + item.companies[companyName],
									};
								}

								return {
									...companyObj,
									[companyName]: item.companies[companyName],
								};
							},
							acc.companies
						);

						const segments = Object.keys(item.segments || {}).reduce(
							(segmentObj, segmentName) => {
								if (segmentObj[segmentName]) {
									return {
										...segmentObj,
										[segmentName]:
											segmentObj[segmentName] + item.segments[segmentName],
									};
								}

								return {
									...segmentObj,
									[segmentName]: item.segments[segmentName],
								};
							},
							acc.segments
						);

						const segmentsByCompanies = Object.keys(
							item.segmentsByCompanies || {}
						).reduce((obj, segmentName) => {
							if (obj[segmentName]) {
								const aggregatedCompaniesBySegment = Object.keys(
									obj[segmentName]
								).reduce((companyAcc, companyName) => {
									if (companyAcc[companyName]) {
										return {
											...companyAcc,
											[companyName]:
												companyAcc[companyName] +
												get(
													item,
													`segmentsByCompanies.${segmentName}.${companyName}`,
													0
												),
										};
									}

									return {
										...companyAcc,
										[companyName]: get(
											item,
											`segmentsByCompanies.${segmentName}.${companyName}`,
											0
										),
									};
								}, acc.segmentsByCompanies[segmentName]);

								return {
									...obj,
									[segmentName]: aggregatedCompaniesBySegment,
								};
							}

							return {
								...obj,
								[segmentName]: item.segmentsByCompanies[segmentName],
							};
						}, acc.segmentsByCompanies);

						return { companies, segments, segmentsByCompanies };
					},
					{ companies: {}, segments: {}, segmentsByCompanies: {} }
				);

				const aggregatedResult = {
					companies: aggregatedProps.companies,
					...(!isEmpty(aggregatedProps.segments)
						? {
								segments: aggregatedProps.segments,
								segmentsByCompanies: aggregatedProps.segmentsByCompanies,
						  }
						: {}),
				};

				return [
					...acc,
					{
						...item,
						spend: aggregatedSpend,
						children: filteredChildren,
						...aggregatedResult,
					},
				];
			}
		}

		return acc;
	}, []);
};

export const sortGroups = (nodes, sort) => {
	if (!sort || !sort.column) {
		return nodes;
	}

	const func = item => {
		return sort.isCompany
			? item.companies[sort.column] || 0
			: item[sort.column];
	};

	const sortedChildren = nodes.map(item => ({
		...item,
		children: orderBy(item.children, func, sort.direction),
	}));

	return orderBy(sortedChildren, func, sort.direction);
};

export const convertInitiativeResp = initiative => {
	return {
		...initiative,
		items: initiative.items.map(item => {
			return {
				...item,
				hierarchy: getHierarchyFromId(item.groupName),
				weightedAverageDpo: getWeightedDpo(item),
				isUsed: true,
				companies: item.companies.reduce((acc, company) => {
					return { ...acc, [company.companyName]: company.spend };
				}, {}),
			};
		}),
	};
};

export const convertPTImprovementResp = resp => {
	return resp.map(item => {
		return {
			...item,
			lowPercentage: 30,
			highPercentage: 60,
			targetDpo: 90,
			groupName: item.id,
			weightedAverageDpo: getWeightedDpo(item),
			companies: item.companies.reduce((acc, company) => {
				return { ...acc, [company.companyName]: company.spend };
			}, {}),
		};
	});
};

export const convertPTHarmonizationResp = resp => {
	return resp.map(item => {
		return {
			...item,
			lowPercentage: 30,
			highPercentage: 60,
			targetDpo: item.harmonizedDpo,
			workingCapital: item.workingCapitalImpact,
			groupName: '',
			companies: item.companies.reduce((acc, company) => {
				return { ...acc, [company.companyName]: company.spend };
			}, {}),
			companiesDpo: item.companies.reduce((acc, company) => {
				return { ...acc, [company.companyName]: company.targetDpo };
			}, {}),
		};
	});
};

export const getStartOfWeek = date => {
	return moment(date)
		.startOf('isoWeek')
		.format('YYYY-MM-DD');
};

export const getWeightedDpo = node => {
	if (!node || !node.dpos) {
		return 0;
	}

	const dpos = isArray(node.dpos) ? node.dpos : node.dpos.originDpos;

	return Math.round(
		dpos.reduce((acc, item) => {
			const weightedDpo = item.dpo * (item.spend / node.spend);

			return acc + weightedDpo;
		}, 0)
	);
};

export const getWorkingCapital = (node, wacc) => {
	if (!node || !node.dpos || !wacc) {
		return 0;
	}

	const dpos = isArray(node.dpos) ? node.dpos : node.dpos.originDpos;

	return dpos.reduce((acc, item) => {
		const difference =
			item.dpo >= node.targetDpo ? 0 : node.targetDpo - item.dpo;
		const workingCapital = (difference / 365) * (wacc / 100) * item.spend;

		return acc + workingCapital;
	}, 0);
};

export const convertSaveData = data => {
	const itemProps =
		data.initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT
			? ['targetDpo']
			: data.initiativeItemType === ITEMS_TYPE.CATEGORY ||
			  data.initiativeItemType === ITEMS_TYPE.VENDOR ||
			  data.initiativeItemType === ITEMS_TYPE.TAIL_SPEND
				? ['addressablePercentage', 'opexPercentage', 'capexPercentage']
				: [];

	return {
		...data,
		items: data.items.map(item => ({
			...pick(
				item,
				...itemProps,
				...(data.initiativeItemType === ITEMS_TYPE.TAIL_SPEND
					? ['tailSegmentsLowHigh']
					: []),
				...(data.initiativeItemType === ITEMS_TYPE.CATEGORY
					? ['includeTailSpend']
					: []),
				'name',
				'groupName',
				'lowPercentage',
				'highPercentage'
			),
			id: isNumber(item.id) ? item.id : undefined,
		})),
	};
};

export const getHierarchyFromTaxonomy = data => {
	if (!data) {
		return [];
	}

	const level = Math.min(data.level, 4);
	const hierarchy = [];

	for (let i = 1; i <= level; i++) {
		hierarchy.push(data.data[`project_L${i}Category`]);
	}

	return hierarchy;
};

export const getRoundedPercentage = value => {
	return Math.round(value * 10000) / 100;
};

const GROUP_ITEM_NAME = 'Others';
export const getChartDataWithGrouping = data => {
	if (data.length > 4) {
		let group = data.slice(3);

		if (group.length > 1) {
			group = [
				{
					name: GROUP_ITEM_NAME,
					color: colors.grey2,
					value:
						Math.round(group.reduce((acc, item) => acc + item.value, 0) * 100) /
						100,
				},
			];
		}

		return [...data.slice(0, 3), ...group];
	}

	return data;
};

export const PLAN_AND_TRACK_NAV_CONFIG = [
	{
		name: 'Initiatives',
		label: 'Initiatives',
		path: '/initiatives/categories',
	},
	{
		name: 'waves',
		label: 'Wave Planning',
		path: '/waves',
	},
	{
		name: 'savings-tracker',
		label: 'Savings Tracker',
		path: '/budget',
	},
];
